<template>
    <BaseSidebar
        :isOpen="isOpen"
        title="Filters"
        @close="$emit('close')"
        :customClasses="[
            'left-0 right-0 top-0 bottom-0 max-w-none w-full rounded-none border-0 ring-0',
            'md:right-3 md:left-auto md:bottom-3 md:top-0 md:max-w-md md:w-auto md:rounded-lg md:border md:border-slate-200 dark:border-slate-700 md:ring-1 md:ring-white md:ring-opacity-5',
        ]"
    >
        <div class="flex flex-col gap-4">
            <template v-if="currentTab === 'transfer-bonus-history'">
                <div
                    class="flex items-center gap-2 px-4 py-2.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
                >
                    <Icon icon="search" class="w-5 h-5 text-slate-800 dark:text-slate-100" />
                    <input
                        type="text"
                        :value="bonusProgramSearchQuery"
                        @input="$emit('update:bonusProgramSearchQuery', $event.target.value)"
                        placeholder="Bonus program"
                        class="flex-1 text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 border-none outline-none bg-transparent p-0"
                    />
                    <button
                        v-if="bonusProgramSearchQuery"
                        @click="$emit('update:bonusProgramSearchQuery', '')"
                        class="flex items-center justify-center w-5 h-5 text-slate-800 dark:text-slate-100 hover:text-slate-600 dark:hover:text-slate-300"
                    >
                        <Icon icon="close" class="w-3 h-3" />
                    </button>
                </div>

                <div
                    class="flex flex-col max-h-[60vh] overflow-y-auto scrollbar-overlay"
                >
                    <ul class="flex flex-col">
                        <li
                            v-if="
                                filteredBonusProgramOptions.length === 0 &&
                                bonusProgramSearchQuery
                            "
                            class="px-4 py-2.5"
                        >
                            <span class="text-slate-900 dark:text-slate-100 text-sm">
                                No results found, try adjusting your search
                            </span>
                        </li>
                        <li
                            v-for="option in filteredBonusProgramOptions"
                            :key="option.id"
                        >
                            <button
                                type="button"
                                @click="toggleBonusProgram(option.id)"
                                class="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-slate-700 focus:bg-gray-50 dark:focus:bg-slate-700"
                            >
                                <input
                                    type="checkbox"
                                    :checked="isBonusProgramSelected(option.id)"
                                    class="rounded text-10x-orange"
                                    readonly
                                />
                                <span class="text-slate-800 dark:text-slate-100 text-sm">
                                    {{ option.name }}
                                </span>
                            </button>
                        </li>
                    </ul>
                </div>

                <div class="flex items-center gap-3 pt-2">
                    <button
                        type="button"
                        @click="clearBonusProgramFilters()"
                        class="flex px-4 py-2 items-center gap-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-700"
                    >
                        <span class="text-slate-800 dark:text-slate-100 text-sm font-medium">
                            Reset
                        </span>
                    </button>
                    <button
                        type="button"
                        @click="$emit('close')"
                        class="flex px-4 py-2 items-center gap-2 rounded bg-orange shadow-sm hover:bg-orange-600"
                    >
                        <span class="text-white text-sm font-medium">
                            Apply
                        </span>
                    </button>
                </div>
            </template>

            <template v-else-if="currentTab === 'current-rates'">
                <div class="flex flex-col gap-2">
                    <h3 class="px-1 text-slate-800 dark:text-slate-100 text-sm font-semibold">
                        Program type
                    </h3>
                    <ul>
                        <li
                            v-for="option in programTypeOptions"
                            :key="option.id"
                        >
                            <button
                                type="button"
                                @click="toggleProgramType(option.id)"
                                class="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-slate-700 focus:bg-gray-50 dark:focus:bg-slate-700 focus:outline-none flex items-center gap-2 text-slate-800 dark:text-slate-100"
                            >
                                <input
                                    type="checkbox"
                                    :checked="selectedProgramTypes.includes(option.id)"
                                    class="rounded text-10x-orange"
                                    readonly
                                />
                                <span>{{ option.name }}</span>
                            </button>
                        </li>
                    </ul>
                    <div
                        v-if="selectedProgramTypes.length >= 1"
                        class="px-1"
                    >
                        <button
                            type="button"
                            @click="clearProgramTypeFilters()"
                            class="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                        >
                            Reset
                        </button>
                    </div>
                </div>

                <div class="flex flex-col gap-2 pt-2">
                    <h3 class="px-1 text-slate-800 dark:text-slate-100 text-sm font-semibold">
                        Added to My Points
                    </h3>

                    <div
                        v-if="isSingleUser"
                        class="flex items-center gap-3 px-4 py-2.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
                    >
                        <span
                            class="text-slate-800 dark:text-slate-100 text-sm leading-5 font-normal flex-1"
                        >
                            Only programs added to My Points
                        </span>
                        <Switch
                            :value="singleUserToggle"
                            label="Only programs added to My Points"
                            @input="$emit('update:singleUserToggle', $event)"
                        />
                    </div>

                    <ul v-else>
                        <li
                            v-for="option in addedToMyPointsOptions"
                            :key="option.id"
                        >
                            <button
                                type="button"
                                @click="toggleAddedToMyPointsSelection(option)"
                                class="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-slate-700 focus:bg-gray-50 dark:focus:bg-slate-700 focus:outline-none flex items-center gap-2 text-slate-800 dark:text-slate-100"
                            >
                                <input
                                    type="checkbox"
                                    :checked="isAddedToMyPointsSelected(option)"
                                    class="rounded text-10x-orange"
                                    readonly
                                />
                                <span>{{ option.name }}</span>
                            </button>
                        </li>
                        <li
                            v-if="selectedAddedToMyPoints.length >= 1"
                            class="px-1 pt-1"
                        >
                            <button
                                type="button"
                                @click="clearAddedToMyPointsFilter()"
                                class="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                            >
                                Reset
                            </button>
                        </li>
                    </ul>
                </div>

                <div class="flex items-center gap-3 pt-2">
                    <button
                        type="button"
                        @click="clearCurrentRatesFilters()"
                        class="flex px-4 py-2 items-center gap-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-700"
                    >
                        <span class="text-slate-800 dark:text-slate-100 text-sm font-medium">
                            Reset
                        </span>
                    </button>
                    <button
                        type="button"
                        @click="$emit('close')"
                        class="flex px-4 py-2 items-center gap-2 rounded bg-orange shadow-sm hover:bg-orange-600"
                    >
                        <span class="text-white text-sm font-medium">
                            Apply
                        </span>
                    </button>
                </div>
            </template>
        </div>
    </BaseSidebar>
</template>

<script setup>
import Icon from '@/components/ui/icon/icon.vue';
import BaseSidebar from '@/components/ui/base-sidebar.vue';
import Switch from '@/components/ui/switch/switch.vue';

defineProps({
    isOpen: Boolean,
    currentTab: String,
    selectedProgramTypes: Array,
    programTypeOptions: Array,
    selectedAddedToMyPoints: Array,
    addedToMyPointsOptions: Array,
    isSingleUser: Boolean,
    singleUserToggle: Boolean,
    bonusProgramSearchQuery: String,
    filteredBonusProgramOptions: Array,
    toggleProgramType: Function,
    clearProgramTypeFilters: Function,
    toggleAddedToMyPointsSelection: Function,
    isAddedToMyPointsSelected: Function,
    clearAddedToMyPointsFilter: Function,
    toggleBonusProgram: Function,
    isBonusProgramSelected: Function,
    clearBonusProgramFilters: Function,
    clearCurrentRatesFilters: Function,
});

defineEmits(['close', 'update:bonusProgramSearchQuery', 'update:singleUserToggle']);
</script>

<style scoped>
.scrollbar-overlay {
    scrollbar-width: thin;
    scrollbar-color: rgba(148, 163, 184, 0.3) transparent;
}

.scrollbar-overlay::-webkit-scrollbar {
    width: 8px;
}

.scrollbar-overlay::-webkit-scrollbar-track {
    background: transparent;
}

.scrollbar-overlay::-webkit-scrollbar-thumb {
    background-color: rgba(148, 163, 184, 0.3);
    border-radius: 4px;
    border: transparent;
}

.scrollbar-overlay::-webkit-scrollbar-thumb:hover {
    background-color: rgba(148, 163, 184, 0.5);
}
</style>
